import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { getKYCList, getKYCStats } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const KYCList = () => {
    const navigate = useNavigate();
    const [kycApplications, setKycApplications] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Filter states
    const [status, setStatus] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(10);

    // Fetch KYC statistics
    const fetchStats = async () => {
        try {
            const response = await getKYCStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Error fetching KYC stats:', error);
        }
    };

    // Fetch KYC applications with filters
    const fetchKYCApplications = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = {
                page: currentPage,
                limit
            };

            if (status !== 'all') params.status = status;
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            if (sortBy) params.sortBy = sortBy;
            if (sortOrder) params.sortOrder = sortOrder;

            const response = await getKYCList(params);
            
            if (response.success) {
                const kycData = response.data?.kycs || response.data?.kycList || (Array.isArray(response.data) ? response.data : []);
                setKycApplications(Array.isArray(kycData) ? kycData : []);
                setTotalPages(response.data?.pagination?.pages || response.pagination?.pages || 1);
            } else {
                throw new Error(response.message || 'Failed to fetch KYC applications');
            }
        } catch (error) {
            console.error('Error fetching KYC applications:', error);
            setError(error.message || 'Failed to fetch KYC applications');
            setKycApplications([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        fetchKYCApplications();
    }, [currentPage, status, startDate, endDate, sortBy, sortOrder]);

    const handleFilterReset = () => {
        setStatus('all');
        setStartDate('');
        setEndDate('');
        setSortBy('createdAt');
        setSortOrder('desc');
        setCurrentPage(1);
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: 'warning',
            approved: 'success',
            rejected: 'danger'
        };
        return badges[status] || 'secondary';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="KYC Applications" sidebutton={true} />

            {/* Statistics Cards */}
            {stats && (
                <div className="row mb-4">
                    <div className="col-xl-3 col-sm-6">
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <h4 className="mb-0">{stats.total || 0}</h4>
                                        <p className="text-muted mb-0">Total KYC</p>
                                    </div>
                                    <div className="text-primary">
                                        <i className="fa fa-file-text fa-2x"></i>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-xl-3 col-sm-6">
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <h4 className="mb-0">{stats.pending || 0}</h4>
                                        <p className="text-muted mb-0">Pending</p>
                                    </div>
                                    <div className="text-warning">
                                        <i className="fa fa-clock-o fa-2x"></i>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-xl-3 col-sm-6">
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <h4 className="mb-0">{stats.approved || 0}</h4>
                                        <p className="text-muted mb-0">Approved</p>
                                    </div>
                                    <div className="text-success">
                                        <i className="fa fa-check-circle fa-2x"></i>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-xl-3 col-sm-6">
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <h4 className="mb-0">{stats.rejected || 0}</h4>
                                        <p className="text-muted mb-0">Rejected</p>
                                    </div>
                                    <div className="text-danger">
                                        <i className="fa fa-times-circle fa-2x"></i>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col-xl-12">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <Card.Title className="mb-0">KYC Applications</Card.Title>
                                {stats && (
                                    <div>
                                        <Badge bg="primary" className="me-2">Total: {stats.total}</Badge>
                                        <Badge bg="warning" className="me-2">Pending: {stats.pending}</Badge>
                                        <Badge bg="success" className="me-2">Approved: {stats.approved}</Badge>
                                        <Badge bg="danger">Rejected: {stats.rejected}</Badge>
                                    </div>
                                )}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {/* Filters */}
                            <Row className="mb-3">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select value={status} onChange={(e) => {
                                            setStatus(e.target.value);
                                            setCurrentPage(1);
                                        }}>
                                            <option value="all">All</option>
                                            <option value="pending">Pending</option>
                                            <option value="approved">Approved</option>
                                            <option value="rejected">Rejected</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Start Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => {
                                                setStartDate(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>End Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => {
                                                setEndDate(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Sort By</Form.Label>
                                        <div className="d-flex gap-2">
                                            <Form.Select 
                                                value={sortBy} 
                                                onChange={(e) => {
                                                    setSortBy(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                                style={{ flex: 1 }}
                                            >
                                                <option value="createdAt">Submission Date</option>
                                                <option value="status">Status</option>
                                            </Form.Select>
                                            <Form.Select 
                                                value={sortOrder} 
                                                onChange={(e) => {
                                                    setSortOrder(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                                style={{ flex: 1 }}
                                            >
                                                <option value="asc">Asc</option>
                                                <option value="desc">Desc</option>
                                            </Form.Select>
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col>
                                    <Button variant="outline-secondary" size="sm" onClick={handleFilterReset}>
                                        <i className="fa fa-refresh me-2"></i>Reset Filters
                                    </Button>
                                </Col>
                            </Row>

                            {error && (
                                <Alert variant="danger" className="mb-3">
                                    <i className="fa fa-exclamation-circle me-2"></i>
                                    {error}
                                </Alert>
                            )}

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table responsive className="table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>User</th>
                                                <th>Document Type</th>
                                                <th>Submission Date</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {kycApplications.length > 0 ? (
                                                kycApplications.map((application) => (
                                                    <tr key={application._id}>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span className="fw-medium">
                                                                    {application.userId?.name || application.user?.name || '-'}
                                                                </span>
                                                                <small className="text-muted">
                                                                    {application.userId?.email || application.user?.email || '-'}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Badge bg="info">
                                                                {application.documentType?.toUpperCase() || '-'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span>{formatDate(application.submittedAt || application.createdAt)}</span>
                                                                <small className="text-muted">
                                                                    {formatDateTime(application.submittedAt || application.createdAt).split(', ')[1]}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Badge bg={getStatusBadge(application.status)}>
                                                                {application.status?.toUpperCase() || '-'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => navigate(`/admin/kyc/${application._id}`)}
                                                            >
                                                                <i className="fa fa-eye me-1"></i> View Details
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-5">
                                                        <div className="text-muted">
                                                            <i className="fa fa-file-text fa-3x mb-3 d-block"></i>
                                                            <p className="mb-0">No KYC applications found</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-4">
                                    <div>
                                        Showing page {currentPage} of {totalPages}
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-primary"
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(prev => prev - 1)}
                                        >
                                            <i className="fa fa-chevron-left me-1"></i> Previous
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            disabled={currentPage >= totalPages}
                                            onClick={() => setCurrentPage(prev => prev + 1)}
                                        >
                                            Next <i className="fa fa-chevron-right me-1"></i>
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default KYCList;
